import { z } from 'zod';
import { registry } from './registry';
export function registerEnum(options, meta) {
    const values = options.map((o) => o.value);
    if (values.length === 0) {
        throw new Error(`registerEnum("${meta.label}") requires at least one option`);
    }
    const enumSchema = z.enum(values);
    const registryMeta = { label: meta.label, options: [...options] };
    // Select inputs emit '' for "nothing selected" rather than `undefined`/`null`. Accepting ''
    // in a union and transforming it to the value each variant actually wants (undefined/null/the
    // default) lets a cleared select pass validation instead of failing with "Invalid option".
    // This builds a brand-new schema rather than modifying `enumSchema` itself, so there's no risk
    // of `.optional()`/`.nullable()`/etc. below accidentally calling themselves.
    const withEmptyString = () => enumSchema.or(z.literal(''));
    const base = enumSchema.register(registry, registryMeta);
    return Object.assign(base, {
        optional: () => withEmptyString()
            .transform((v) => (v === '' ? undefined : v))
            .optional()
            .register(registry, registryMeta),
        nullable: () => withEmptyString()
            .transform((v) => (v === '' ? null : v))
            .nullable()
            .register(registry, registryMeta),
        nullish: () => withEmptyString()
            .transform((v) => (v === '' ? undefined : v))
            .nullish()
            .register(registry, registryMeta),
        default: (value) => withEmptyString()
            .transform((v) => (v === '' ? value : v))
            // TS can't prove `T[number]['value']` matches itself through the generic
            // `NoUndefined<Awaited<...>>` wrapper Zod's `.default()` overloads use — safe to
            // assert since `value` is already constrained to `T[number]['value']` above.
            .default(value)
            .register(registry, registryMeta),
    });
}
export function registerEnumArray(options, meta) {
    const itemEnum = registerEnum(options, meta);
    const arraySchema = z.array(itemEnum);
    const registryMeta = { label: meta.label, options: [...options] };
    const optionalSchema = arraySchema.optional.bind(arraySchema);
    const base = arraySchema.register(registry, registryMeta);
    return Object.assign(base, {
        optional: () => optionalSchema().register(registry, registryMeta),
    });
}
